import type { Core } from "@/client/core/Core.api";
import type { LabsAudioResource } from "@/client/core/platform-types/labs.api";
import { postToServer } from "@/client/utils/postToServer";
import { uploadFilesToS3 } from "@/client/utils/s3Upload";
import {
    kSpliceSearchEndpoint,
    SpliceAssetCategory,
    toAssetCategoryParam,
    toSpliceSearchKeyParam,
    toSpliceSearchChordTypeParam,
    type SpliceSearchResult,
    type SpliceSearchSuccessResponse,
} from "@/shared/splice-search.api";
import { toMusicalKey, toChordType } from "@/shared/musical-key.api";

const kDefaultResultsPerPage = 10;
const kMinQueryLength = 2;

export interface SpliceSoundsSearchServiceOptions {
    /**
     * Results per Atlantis page. The default suits a picker that shows a
     * handful at a time; a browsing list asks for more per round trip.
     * Atlantis caps a page at 16 and rejects anything larger.
     */
    readonly resultsPerPage?: number;
}

export interface SearchResponse {
    readonly results: readonly SpliceSearchResult[];
    readonly tagSummary: readonly string[];
}

interface SearchState {
    query: string;
    page: number;
    results: readonly SpliceSearchResult[];
    category: SpliceAssetCategory;
    instrument: string;
    genre: string;
    key: string;
    chordType: string;
    bpmMin: number | undefined;
    bpmMax: number | undefined;
    sort: string;
    order: string;
    seedFile: File | undefined;
    seedFileKey: string | undefined;
    seedBpm: number | undefined;
    excludeQuerySemantic: boolean;
    /** Tags to filter by beyond the single instrument and genre: what a tag row toggles. */
    queryTags: readonly string[];
}

export const createSpliceSoundsSearchService = (
    core: Core,
    options: SpliceSoundsSearchServiceOptions = {},
) => {
    const resultsPerPage = options.resultsPerPage ?? kDefaultResultsPerPage;
    const state: SearchState = {
        query: "",
        page: 1,
        results: [],
        category: SpliceAssetCategory.All,
        instrument: "",
        genre: "",
        key: "",
        chordType: "",
        bpmMin: undefined,
        bpmMax: undefined,
        sort: "",
        order: "",
        seedFile: undefined,
        seedFileKey: undefined,
        seedBpm: undefined,
        excludeQuerySemantic: false,
        queryTags: [],
    };

    const buildQueryTags = (): string[] => {
        const tags = new Set<string>();
        if (state.instrument !== "") {
            tags.add(state.instrument);
        }
        if (state.genre !== "") {
            tags.add(state.genre);
        }
        for (const tag of state.queryTags) {
            if (tag !== "") {
                tags.add(tag);
            }
        }
        return [...tags];
    };

    // Upload the seed file (once per file) and cache the resulting S3 key so
    // paging/re-searching the same seed doesn't re-upload.
    const ensureSeedKey = async (): Promise<string | undefined | Error> => {
        if (state.seedFile === undefined) {
            return undefined;
        }
        if (state.seedFileKey !== undefined) {
            return state.seedFileKey;
        }
        const keysOrError = await uploadFilesToS3({ seedAudioFileKey: state.seedFile });
        if (keysOrError instanceof Error) {
            return keysOrError;
        }
        state.seedFileKey = keysOrError.seedAudioFileKey;
        return state.seedFileKey;
    };

    const fetchPage = async (
        query: string,
        page: number,
    ): Promise<SpliceSearchSuccessResponse | Error> => {
        const seedKeyOrError = await ensureSeedKey();
        if (seedKeyOrError instanceof Error) {
            return seedKeyOrError;
        }
        const seedAudioFileKey = seedKeyOrError;

        const queryTags = buildQueryTags();
        const keyParam = toSpliceSearchKeyParam(state.key);
        const chordTypeParam = toSpliceSearchChordTypeParam(state.chordType);

        const requestBody = {
            query,
            count: resultsPerPage,
            page,
            assetCategory: toAssetCategoryParam(state.category),
            queryTags: queryTags.length > 0 ? queryTags : undefined,
            key: keyParam,
            chordType: chordTypeParam,
            bpmMin: state.bpmMin,
            bpmMax: state.bpmMax,
            sort: state.sort !== "" ? state.sort : undefined,
            order: state.order !== "" ? state.order : undefined,
            seedAudioFileKey,
            bpm: state.seedBpm,
            excludeQuerySemantic: state.excludeQuerySemantic || undefined,
        };

        // eslint-disable-next-line no-console
        console.log("[splice-search] request", JSON.parse(JSON.stringify(requestBody)));

        const result = await postToServer(kSpliceSearchEndpoint, requestBody);

        if (result instanceof Error) {
            return result;
        }

        return result;
    };

    const search = async (query: string, pageCount = 1): Promise<SearchResponse | Error> => {
        state.query = query;
        state.page = 1;
        state.results = [];

        const firstPage = await fetchPage(query, 1);
        if (firstPage instanceof Error) {
            return firstPage;
        }

        let combined: readonly SpliceSearchResult[] = firstPage.results;

        if (pageCount > 1) {
            const extraPages = await Promise.all(
                Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(query, i + 2)),
            );
            for (const result of extraPages) {
                if (result instanceof Error) {
                    return result;
                }
                combined = [...combined, ...result.results];
            }
            state.page = pageCount;
        }

        state.results = combined;
        return { results: combined, tagSummary: firstPage.tagSummary };
    };

    const loadMore = async (): Promise<SearchResponse | undefined | Error> => {
        if (!canSearch()) {
            return undefined;
        }

        const nextPage = state.page + 1;
        const result = await fetchPage(state.query, nextPage);
        if (result instanceof Error) {
            return result;
        }

        if (result.results.length === 0) {
            return undefined;
        }

        state.page = nextPage;
        state.results = [...state.results, ...result.results];
        return { results: state.results, tagSummary: result.tagSummary };
    };

    const setAssetCategory = (category: SpliceAssetCategory): void => {
        state.category = category;
    };

    const setInstrument = (value: string): void => {
        state.instrument = value;
    };

    const setGenre = (value: string): void => {
        state.genre = value;
    };

    const setQueryTags = (tags: readonly string[]): void => {
        state.queryTags = tags;
    };

    const setKey = (value: string): void => {
        state.key = value;
    };

    const setChordType = (value: string): void => {
        state.chordType = value;
    };

    const setBpmMin = (value: number | undefined): void => {
        state.bpmMin = value;
    };

    const setBpmMax = (value: number | undefined): void => {
        state.bpmMax = value;
    };

    const setSort = (value: string): void => {
        state.sort = value;
    };

    const setOrder = (value: string): void => {
        state.order = value;
    };

    const setExcludeQuerySemantic = (exclude: boolean): void => {
        state.excludeQuerySemantic = exclude;
    };

    const setSeedFile = (file: File | undefined): void => {
        if (state.seedFile === file) {
            return;
        }
        state.seedFile = file;
        state.seedFileKey = undefined;
    };

    const setSeedBpm = (bpm: number | undefined): void => {
        state.seedBpm = bpm;
    };

    const hasSeedFile = (): boolean => state.seedFile !== undefined;

    const getQuery = (): string => state.query;

    /** The Atlantis page the results in hand run to. */
    const getPage = (): number => state.page;

    /**
     * Takes back a result set from an earlier page of the site, so paging
     * carries on from where it was without a search being re-run.
     */
    const restoreSession = (
        query: string,
        results: readonly SpliceSearchResult[],
        page: number,
    ): void => {
        state.query = query;
        state.results = results;
        state.page = Math.max(1, Math.floor(page));
    };

    const canSearch = (): boolean =>
        state.seedFile !== undefined ||
        state.query.length >= kMinQueryLength ||
        buildQueryTags().length > 0 ||
        state.key !== "" ||
        state.chordType !== "" ||
        state.bpmMin !== undefined ||
        state.bpmMax !== undefined;

    const loadResult = async (result: SpliceSearchResult): Promise<LabsAudioResource[] | Error> => {
        const response = await fetch(result.previewUrl);
        if (!response.ok) {
            return new Error(`Failed to fetch preview: ${response.status}`);
        }
        const blob = await response.blob();
        const mimeType = blob.type.startsWith("audio/") ? blob.type : "audio/mpeg";
        const extension = mimeType === "audio/mpeg" ? ".mp3" : ".wav";
        const fileName = `${result.metadata.name}${extension}`;
        return core.actions.addFiles([
            {
                file: new File([blob], fileName, { type: mimeType }),
                type: "wav",
                metadata: {
                    bpm: result.metadata.bpm > 0 ? result.metadata.bpm : null,
                    artworkUrl:
                        result.metadata.packArtworkUrl !== ""
                            ? result.metadata.packArtworkUrl
                            : null,
                    key: toMusicalKey(result.metadata.key) ?? null,
                    chordType: toChordType(result.metadata.chordType) ?? null,
                    type: null,
                    spliceUuid: result.metadata.uuid,
                    tags: result.metadata.tags.slice(0),
                },
            },
        ]);
    };

    return {
        search,
        loadMore,
        setAssetCategory,
        setInstrument,
        setGenre,
        setQueryTags,
        setKey,
        setChordType,
        setBpmMin,
        setBpmMax,
        setSort,
        setOrder,
        setExcludeQuerySemantic,
        setSeedFile,
        setSeedBpm,
        hasSeedFile,
        getQuery,
        getPage,
        restoreSession,
        canSearch,
        loadResult,
    };
};

export type SpliceSoundsSearchService = ReturnType<typeof createSpliceSoundsSearchService>;
